import type React from "react"
import { useCallback, useRef, useContext, useState } from "react"
import { Upload, ImageIcon, Loader } from "lucide-react"
import styles from "./logoUploader.module.css"
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext"
import { AuthContext } from "../../../../../../../../contexts/AuthContext"
import { apiImageUpload } from "../../../../../../../../sdk"

import { ImageWithAuth } from "../../../../../../../Common/MarkdownDisplay/components/ImageWithAuth";

export default function LogoUpload() {
  const { template, setTemplate } = useContext(TemplateContext)
  const { token } = useContext(AuthContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'uploading' | 'deleting'>('idle')
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Update template with logo URL
  const updateTemplateLogo = useCallback((logoUrl: string | null) => {
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return prevTemplate;
      return {
        ...prevTemplate,
        Logo: logoUrl || "",
      };
    });
  }, [setTemplate])

  const handleFile = useCallback(async (file: File) => {
    if (!template || !template.ID) {
      setUploadError("Please save the template first before uploading a logo")
      return
    }
  
    setStatus('uploading')
    setUploadError(null)
    
    try {
      // Upload image and get URL directly
      const response = await apiImageUpload(token, file)
      
      // Debug the response structure
      console.log("Image upload response:", response)
      
      const url = import.meta.env.VITE_API_URL + "/" + response.data.filePath
      console.log("url:", url)
      if (!url) {
        throw new Error("No URL returned from upload")
      }
      
      // Save the URL returned from the API
      updateTemplateLogo(url)
    } catch (error) {
      console.error("Error uploading logo:", error)
      setUploadError("Failed to upload logo. Please try again.")
    } finally {
      setStatus('idle')
    }
  }, [token, template, updateTemplateLogo])

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFile(file)
      event.target.value = ""
    }
  }, [handleFile])

  // Combined drag handlers
  const handleDragEvent = useCallback((e: React.DragEvent<HTMLDivElement>, isDragEnter: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList[isDragEnter ? 'add' : 'remove'](styles.dragging)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.remove(styles.dragging)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleRemove = useCallback(() => {
    setStatus('deleting')
    setUploadError(null)
    
    try {
      // Simply clear the logo URL from the template
      updateTemplateLogo(null)
      setStatus('idle')
    } catch (error) {
      console.error("Error removing logo:", error)
      setUploadError("Failed to remove logo. Please try again.")
      setStatus('idle')
    }
  }, [updateTemplateLogo])

  const isLoading = status !== 'idle';
  const statusMessage = 
    status === 'uploading' ? "Uploading logo..." : 
    status === 'deleting' ? "Removing logo..." : 
    status === 'loading' ? "Loading logo..." : "";

  // Show loading state if template is null
  if (!template) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <ImageIcon className={styles.icon} />
          <h1>Logo Upload</h1>
        </div>
        <div className={styles.uploadSection}>
          <div className={styles.previewContainer}>
            <div className={styles.placeholder}>
              <Loader className={`${styles.placeholderIcon} ${styles.spinner}`} />
              <p className={styles.placeholderText}>Loading template data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.header}>
        <ImageIcon className={styles.icon} />
        <h1>Logo Upload</h1>
      </div>

      <div
        className={styles.uploadSection}
        onDragEnter={(e) => handleDragEvent(e, true)}
        onDragLeave={(e) => handleDragEvent(e, false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {isLoading ? (
          <div className={styles.previewContainer}>
            <div className={styles.placeholder}>
              <Loader className={`${styles.placeholderIcon} ${styles.spinner}`} />
              <p className={styles.placeholderText}>{statusMessage}</p>
            </div>
          </div>
        ) : template.Logo ? (
          <div className={styles.previewContainer} style={{ backgroundColor: template.BackgroundColor }}>
            <ImageWithAuth 
              src={template.Logo} 
              alt="Uploaded logo" 
              className={styles.preview}
            />
          </div>
        ) : (
          <div className={styles.previewContainer}>
            <div className={styles.placeholder}>
              <ImageIcon className={styles.placeholderIcon} />
              <p className={styles.placeholderText}>
                {template.ID ? "Drag & drop your file here" : "Select template to enable logo upload"}
              </p>
              {uploadError && <p className={styles.errorText}>{uploadError}</p>}
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button 
            className={styles.uploadButton} 
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || !template.ID}
          >
            <input
              ref={fileInputRef}
              type="file"
              className={styles.fileInput}
              onChange={handleFileUpload}
              accept="image/*"
            />
            <Upload className={styles.buttonIcon} />
            {status === 'uploading' ? 'Uploading...' : 'Upload Logo'}
          </button>
          
          {template.Logo && (
            <button 
              className={styles.removeButton} 
              onClick={handleRemove} 
              disabled={isLoading}
            >
              {status === 'deleting' ? 'Removing...' : 'Remove'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}